import React, { useState } from 'react';
import { formatNumber, formatRupees, formatDate } from '../utils/helpers';
import CampaignPreview from './CampaignPreview';
import { ChevronDown, ChevronUp, Edit2, Upload, Save, X } from 'lucide-react';
import { updateCampaignApi } from '../utils/api';

const MetricCard = ({ campaign, brand, slot, cohort, showToast, onUpdate }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMetrics, setEditMetrics] = useState({
    sent: campaign.metrics?.sent || 0,
    delivered: campaign.metrics?.delivered || 0,
    seen: campaign.metrics?.seen || 0,
    clicks: campaign.metrics?.clicks || 0,
    spends: campaign.metrics?.spends || 0,
    revenue: campaign.metrics?.revenue || 0,
  });
  const [saving, setSaving] = useState(false);

  // Calculate derived metrics
  const metrics = campaign.metrics || {};
  const sent = metrics.sent || 0;
  const delivered = metrics.delivered || 0;
  const seen = metrics.seen || 0;
  const clicks = metrics.clicks || 0;
  const revenue = metrics.revenue || 0;

  const deliveryRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0;
  const spends = (delivered * 0.98).toFixed(2);
  const roas = parseFloat(spends) > 0 ? (revenue / parseFloat(spends)).toFixed(2) : 0;

  const handleSaveMetrics = async () => {
    setSaving(true);
    try {
      const response = await updateCampaignApi(campaign.id, {
        metrics: {
          sent: parseInt(editMetrics.sent) || 0,
          delivered: parseInt(editMetrics.delivered) || 0,
          seen: parseInt(editMetrics.seen) || 0,
          clicks: parseInt(editMetrics.clicks) || 0,
          spends: parseFloat(editMetrics.spends) || 0,
          revenue: parseFloat(editMetrics.revenue) || 0,
          delivery_rate: parseInt(editMetrics.sent) > 0 
            ? ((parseInt(editMetrics.delivered) / parseInt(editMetrics.sent)) * 100).toFixed(2)
            : 0
        }
      });
      
      if (showToast) showToast('✅ Metrics updated successfully', 'success');
      setShowEditModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      if (showToast) showToast('Failed to update metrics', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div 
            className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Edit Campaign Metrics</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Sent</label>
                <input
                  type="number"
                  value={editMetrics.sent}
                  onChange={(e) => setEditMetrics({ ...editMetrics, sent: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Delivered</label>
                <input
                  type="number"
                  value={editMetrics.delivered}
                  onChange={(e) => setEditMetrics({ ...editMetrics, delivered: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Seen</label>
                <input
                  type="number"
                  value={editMetrics.seen}
                  onChange={(e) => setEditMetrics({ ...editMetrics, seen: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Clicks</label>
                <input
                  type="number"
                  value={editMetrics.clicks}
                  onChange={(e) => setEditMetrics({ ...editMetrics, clicks: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Spends (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editMetrics.spends}
                  onChange={(e) => setEditMetrics({ ...editMetrics, spends: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Revenue (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editMetrics.revenue}
                  onChange={(e) => setEditMetrics({ ...editMetrics, revenue: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveMetrics}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-xl text-white font-medium transition-all disabled:opacity-50"
                style={{ backgroundColor: '#32B4AC' }}
                onMouseEnter={(e) => !saving && (e.target.style.backgroundColor = '#2A9A93')}
                onMouseLeave={(e) => !saving && (e.target.style.backgroundColor = '#32B4AC')}
              >
                {saving ? 'Saving...' : '💾 Save Metrics'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-white font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
      {/* Header with Slot and Date */}
      <div className="bg-slate-750 px-6 py-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Slot Info */}
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: slot?.bg_color || '#3b82f6' }}
              />
              <div>
                <p className="text-white font-semibold">{slot?.name || 'Unknown Slot'}</p>
                <p className="text-xs text-slate-400">{slot?.time}</p>
              </div>
            </div>
            
            {/* Separator */}
            <div className="w-px h-8 bg-slate-700" />
            
            {/* Date */}
            <div>
              <p className="text-xs text-slate-500">Campaign Date</p>
              <p className="text-white font-medium">{formatDate(campaign.date)}</p>
            </div>

            {/* Brand Badge */}
            {brand && (
              <>
                <div className="w-px h-8 bg-slate-700" />
                <div
                  className="px-3 py-1 rounded-full text-white text-sm font-medium"
                  style={{ backgroundColor: brand.color }}
                >
                  {brand.name}
                </div>
              </>
            )}
          </div>

          {/* Toggle Preview Button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all hover:shadow-md"
              style={{ backgroundColor: 'rgba(50, 180, 172, 0.15)', color: '#32B4AC' }}
              title="Edit Metrics"
            >
              <Edit2 size={16} className="inline mr-1" />
              Edit
            </button>
            
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-xl transition-colors"
            >
              {showPreview ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Creative and Content Preview */}
          <div className="space-y-4">
            {/* Creative Image */}
            {campaign.creative && (
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-2">📸 Creative</h4>
                <img
                  src={campaign.creative}
                  alt="Campaign creative"
                  className="w-full rounded-lg border border-slate-700"
                />
              </div>
            )}

            {/* Content Snapshot */}
            {showPreview && (
              <div>
                <h4 className="text-sm font-semibold text-slate-300 mb-2">💬 Content Snapshot</h4>
                <CampaignPreview
                  creative={campaign.creative}
                  contentText={campaign.content_text}
                  contentCta={campaign.content_cta}
                  brandColor={brand?.color}
                />
              </div>
            )}

            {/* Cohort Info */}
            <div className="p-4 bg-slate-900 rounded-lg">
              <h4 className="text-sm font-semibold text-slate-300 mb-2">👥 Cohort</h4>
              <p className="text-white font-medium">{cohort?.name || 'Unknown Cohort'}</p>
              {cohort && (
                <div className="mt-2 flex items-center gap-4 text-sm text-slate-400">
                  <span>{formatNumber(cohort.user_count)} users</span>
                  <span>•</span>
                  <span>{cohort.user_type}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Metrics */}
          <div>
            <h4 className="text-sm font-semibold text-slate-300 mb-4">📊 Campaign Metrics</h4>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Sent */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Sent</p>
                <p className="text-2xl font-bold text-white">{formatNumber(sent)}</p>
              </div>

              {/* Delivered */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Delivered</p>
                <p className="text-2xl font-bold text-white">{formatNumber(delivered)}</p>
              </div>

              {/* Seen */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Seen</p>
                <p className="text-2xl font-bold text-white">{formatNumber(seen)}</p>
              </div>

              {/* Clicks */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Clicks</p>
                <p className="text-2xl font-bold text-white">{formatNumber(clicks)}</p>
              </div>

              {/* Delivery Rate */}
              <div className="p-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg">
                <p className="text-xs text-green-300 mb-1">Delivery Rate</p>
                <p className="text-2xl font-bold text-green-400">{deliveryRate}%</p>
                <p className="text-xs text-green-300 mt-1">{formatNumber(delivered)} / {formatNumber(sent)}</p>
              </div>

              {/* Spends */}
              <div className="p-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg">
                <p className="text-xs text-yellow-300 mb-1">Spends</p>
                <p className="text-2xl font-bold text-yellow-400">{formatRupees(parseFloat(spends))}</p>
                <p className="text-xs text-yellow-300 mt-1">₹0.98 per delivery</p>
              </div>

              {/* Revenue */}
              <div className="p-4 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-lg">
                <p className="text-xs text-blue-300 mb-1">Revenue</p>
                <p className="text-2xl font-bold text-blue-400">{formatRupees(revenue)}</p>
              </div>

              {/* ROAS */}
              <div className="p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg">
                <p className="text-xs text-purple-300 mb-1">ROAS</p>
                <p className="text-2xl font-bold text-purple-400">{roas}x</p>
                <p className="text-xs text-purple-300 mt-1">Return on Ad Spend</p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Status:</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium">
                  {campaign.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* End Main Card */}
    </>
  );
};

export default MetricCard;
